import { ButtonHTMLAttributes, ReactNode, RefObject } from 'react';
import { VariantProps, tv } from 'tailwind-variants';

import Loading from '../atoms/Loading';

const button = tv({
  base: 'cw-text-box-none focus-visible relative inline-flex h-10 items-center justify-center rounded-lg px-4 text-center transition-all duration-200 ease-in-out hover:no-underline',
  variants: {
    variant: {
      animate: '',
      pose: '',
      neutral: '',
      white: '',
    },
    disabled: {
      true: 'cursor-not-allowed opacity-50',
      false: '',
    },
    loading: {
      true: 'cursor-default',
      false: '',
    },
    active: {
      true: '',
      false: 'bg-gray-30 dark:bg-gray-60',
    },
    borderRadius: {
      full: 'rounded-full',
      lg: 'rounded-lg',
      '2xl': 'rounded-2xl',
    },
  },
  compoundVariants: [
    /* Colors */
    {
      active: true,
      variant: 'animate',
      className: 'bg-green-35 text-gray-90',
    },
    {
      active: true,
      variant: 'pose',
      className: 'bg-violet text-white',
    },
    {
      active: true,
      variant: 'neutral',
      className: 'bg-black text-white',
    },
    {
      active: true,
      variant: 'white',
      className: 'bg-white text-black',
    },
    /* Hover styles */
    {
      active: true,
      disabled: false,
      loading: false,
      variant: 'animate',
      className:
        'text-gray-90 hover:text-gray-90 cursor-pointer hover:bg-green-50 active:scale-[0.98] dark:hover:bg-green-50',
    },
    {
      active: true,
      disabled: false,
      loading: false,
      variant: 'pose',
      className:
        'cursor-pointer bg-[#5900F5] text-white hover:bg-[#4600C2] hover:text-white active:scale-[0.98] dark:hover:bg-[#4600C2]',
    },
    {
      active: true,
      disabled: false,
      loading: false,
      variant: 'pose',
      className:
        'cursor-pointer hover:bg-[#4600C2] hover:text-white active:scale-[0.98] dark:hover:bg-[#4600C2]',
    },
    {
      active: true,
      disabled: false,
      loading: false,
      variant: 'neutral',
      className:
        'hover:bg-gray-90 dark:hover:bg-gray-90 cursor-pointer hover:text-white active:scale-[0.98]',
    },
    {
      active: true,
      disabled: false,
      loading: false,
      variant: 'white',
      className:
        'hover:bg-gray-30 dark:hover:bg-gray-30 cursor-pointer hover:text-black active:scale-[0.98]',
    },
    {
      active: false,
      disabled: false,
      loading: false,
      className:
        'hover:bg-gray-45 dark:bg-gray-60 dark:hover:bg-gray-75 cursor-pointer active:scale-[0.98]',
    },
  ],
  defaultVariants: {
    disabled: false,
    loading: false,
    active: true,
    variant: 'animate',
  },
});

export type GoButtonVariants = VariantProps<typeof button>['variant'];

/**
 * Cartwheel's go button component. This is used in play/pause and animate buttons.
 * @see {@link https://studio.getcartwheel.com/ui?selected=GoButtonShowcase}
 */
export default function GoButton({
  children,
  className,
  disabled,
  icon,
  borderRadius = 'lg',
  active = true,
  loading,
  variant = 'animate',
  ref,
  ...props
}: {
  active?: boolean;
  loading?: boolean;
  icon?: ReactNode;
  borderRadius?: VariantProps<typeof button>['borderRadius'];
  variant?: GoButtonVariants;
  ref?: RefObject<HTMLButtonElement>;
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      className={button({
        disabled,
        loading,
        active,
        variant,
        className,
        borderRadius,
      })}
      ref={ref}
      tabIndex={disabled || loading ? -1 : 0}
      disabled={disabled}
      {...props}
    >
      {loading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <Loading />
        </span>
      )}
      {children && (
        <span
          className={`truncate text-[14px]/[14px] font-medium ${
            loading ? 'opacity-0' : ''
          }`}
        >
          {children}
        </span>
      )}
      {icon && !children && (
        <span className={`${loading ? 'opacity-0' : ''}`}>{icon}</span>
      )}
    </button>
  );
}
